/**
 * Analytics Proxy Utility
 * Provides fallback to server-side proxy when PostHog is blocked
 * OPTIMIZED: Non-blocking implementation to prevent UI delays
 */

import posthog from 'posthog-js';
import getConfig from 'next/config';
import { setEnvironmentForFeatureFlags } from './analytics-init';
import logger from '../utils/logger';
// Track if PostHog is blocked (in-memory fallback)
let isPostHogBlocked = false;
let blockCheckComplete = false;
let blockCheckInProgress = false;

/**
 * Get the blocked status from session storage or perform check
 * Uses sessionStorage to persist the result for the entire session
 */
function getBlockedStatus(): boolean {
    // Check session storage first
    if (typeof window !== 'undefined' && window.sessionStorage) {
        const cached = sessionStorage.getItem('posthog_blocked_status');
        if (cached !== null) {
            // Use cached result from this session
            isPostHogBlocked = cached === 'true';
            blockCheckComplete = true;
            return isPostHogBlocked;
        }
    }

    // If no cached result, perform the check
    if (!blockCheckComplete && !blockCheckInProgress) {
        performBlockCheck();
    }

    // Return current state (may be default false on first call)
    return isPostHogBlocked;
}

/**
 * Perform the actual block check and cache the result
 */
function performBlockCheck(): void {
    if (blockCheckInProgress) {
        return;
    }

    blockCheckInProgress = true;

    // Run check asynchronously without blocking
    setTimeout(async () => {
        try {
            const img = new Image();
            const promise = new Promise((resolve, reject) => {
                img.onload = () => resolve(true);
                img.onerror = () => reject(new Error('Blocked'));
                setTimeout(() => reject(new Error('Timeout')), 250); // Ultra-fast timeout for non-blocking
            });

            img.src = 'https://us.posthog.com/static/favicon.ico?' + Date.now();

            await promise;
            isPostHogBlocked = false;
            logger.debug('[Analytics] PostHog is accessible directly');
        } catch (error) {
            logger.debug(
                '[Analytics] PostHog appears to be blocked, using proxy-only mode for this session'
            );
            isPostHogBlocked = true;
        }

        // Cache the result in session storage
        if (typeof window !== 'undefined' && window.sessionStorage) {
            sessionStorage.setItem(
                'posthog_blocked_status',
                String(isPostHogBlocked)
            );
        }

        blockCheckComplete = true;
        blockCheckInProgress = false;
    }, 0);
}

/**
 * Check if PostHog is blocked by ad blockers (NON-BLOCKING)
 * Returns cached result from session storage if available
 */
export function checkPostHogBlocked(): boolean {
    return getBlockedStatus();
}

/**
 * Mark PostHog as blocked and cache the status
 * Used when PostHog fails after initialization
 */
export function markPostHogAsBlocked(): void {
    isPostHogBlocked = true;
    blockCheckComplete = true;

    // Cache the result in session storage
    if (typeof window !== 'undefined' && window.sessionStorage) {
        sessionStorage.setItem('posthog_blocked_status', 'true');
    }

    logger.debug(
        '[Analytics] PostHog marked as blocked, using proxy-only mode'
    );
}

/**
 * Send event through proxy (NON-BLOCKING)
 * Uses beacon API or background fetch to avoid blocking UI
 */
function sendEventViaProxy(
    eventName: string,
    properties?: Record<string, any>
): void {
    // Get or generate distinct ID
    const distinctId =
        localStorage.getItem('pair_user_id') ||
        localStorage.getItem('posthog_distinct_id') ||
        `anonymous_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Get runtime config for environment (at runtime, not build time)
    const { publicRuntimeConfig } = getConfig() || {};
    const config = publicRuntimeConfig?.niioConfig || {};

    // Try to get session ID from PostHog if available (for session recording linking)
    let sessionId: string | undefined;
    try {
        sessionId =
            posthog?.get_session_id?.() ||
            (posthog?.sessionRecording as any)?.sessionId ||
            sessionStorage.getItem('ph_session_id') ||
            undefined;
    } catch (e) {
        // Ignore errors getting session ID
    }

    const payload = {
        event: eventName,
        distinct_id: distinctId,
        properties: {
            ...properties,
            // Include super properties that PostHog would have added
            environment: config.env || 'dev',
            app_name: 'pair',
            app_version: process.env.npm_package_version || '1.0.0',
            domain: window.location.hostname,
            // Standard PostHog properties
            $current_url: window.location.href,
            $pathname: window.location.pathname,
            $host: window.location.hostname,
            $referrer: document.referrer,
            timestamp: new Date().toISOString(),
            // Session recording linkage - critical for viewing recordings from experiments
            ...(sessionId && { $session_id: sessionId }),
        },
        timestamp: new Date().toISOString(),
    };

    // Try beacon API first (guaranteed delivery, non-blocking)
    if ('sendBeacon' in navigator) {
        const blob = new Blob([JSON.stringify(payload)], {
            type: 'application/json',
        });
        const sent = navigator.sendBeacon('/api/analytics', blob);
        if (sent) {
            logger.debug(`[Analytics] Event sent via beacon: ${eventName}`);
            return;
        }
    }

    // Fallback to fetch with keepalive (non-blocking)
    fetch('/api/analytics', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        keepalive: true, // Ensures request completes even if page unloads
    })
        .then((response) => {
            if (response.ok) {
                logger.debug(`[Analytics] Event sent: ${eventName}`);
            }
        })
        .catch((error) => {
            // Silently fail - analytics should never break the app
            logger.debug('[Analytics] Failed to send event:', error);
        });
}

/**
 * Capture analytics event with automatic fallback (NON-BLOCKING)
 * Returns immediately without waiting for network requests
 */
export function captureEvent(
    eventName: string,
    properties?: Record<string, any>
): void {
    // Get blocked status from session cache or check
    const isBlocked = getBlockedStatus();

    // If PostHog is blocked, skip it entirely and use proxy
    if (isBlocked) {
        sendEventViaProxy(eventName, properties);
        return;
    }

    // Only try PostHog if we know it's not blocked
    try {
        if (typeof window !== 'undefined' && posthog && posthog.capture) {
            // PostHog.capture is already non-blocking
            posthog.capture(eventName, properties);
            logger.debug(`[Analytics] Event queued: ${eventName}`);
        } else {
            // PostHog not initialized, use proxy (non-blocking)
            sendEventViaProxy(eventName, properties);
        }
    } catch (error) {
        // If PostHog fails, mark as blocked for this session and use proxy
        logger.debug(
            '[Analytics] PostHog failed, marking as blocked for session'
        );
        if (typeof window !== 'undefined' && window.sessionStorage) {
            sessionStorage.setItem('posthog_blocked_status', 'true');
        }
        isPostHogBlocked = true;
        blockCheckComplete = true;
        sendEventViaProxy(eventName, properties);
    }
}

/**
 * Identify user with automatic fallback (NON-BLOCKING)
 */
export function identifyUser(
    userId: string,
    properties?: Record<string, any>
): void {
    // Store user ID immediately (synchronous)
    localStorage.setItem('pair_user_id', userId);
    if (properties?.email) {
        localStorage.setItem('pair_user_email', properties.email);
    }

    // Get blocked status from session cache
    const isBlocked = getBlockedStatus();

    // If PostHog is blocked, skip it entirely and use proxy
    if (isBlocked) {
        sendEventViaProxy('$identify', {
            ...properties,
            distinct_id: userId,
        });
        return;
    }

    // Only try PostHog if we know it's not blocked
    try {
        if (typeof window !== 'undefined' && posthog && posthog.identify) {
            posthog.identify(userId, properties);
            logger.debug(`[Analytics] User identified: ${userId}`);

            // Set environment for feature flags (critical for new users during signup)
            const { publicRuntimeConfig } = getConfig() || {};
            const config = publicRuntimeConfig?.niioConfig || {};
            setEnvironmentForFeatureFlags(config);

            // Session recording is already running for all users, no need to start it here
        } else {
            // PostHog not initialized, use proxy (non-blocking)
            sendEventViaProxy('$identify', {
                ...properties,
                distinct_id: userId,
            });
        }
    } catch (error) {
        // If PostHog fails, mark as blocked for this session and use proxy
        logger.debug(
            '[Analytics] Identify failed, marking as blocked for session'
        );
        if (typeof window !== 'undefined' && window.sessionStorage) {
            sessionStorage.setItem('posthog_blocked_status', 'true');
        }
        isPostHogBlocked = true;
        blockCheckComplete = true;
        sendEventViaProxy('$identify', {
            ...properties,
            distinct_id: userId,
        });
    }
}

/**
 * Track page view with automatic fallback (NON-BLOCKING)
 */
export function trackPageView(url?: string): void {
    const pageUrl = url || window.location.href;
    const pathname = new URL(pageUrl, window.location.origin).pathname;

    // Fire and forget - no await
    captureEvent('$pageview', {
        $current_url: pageUrl,
        $pathname: pathname,
    });
}

// Export convenience methods
export const analytics = {
    capture: captureEvent,
    identify: identifyUser,
    pageView: trackPageView,
    checkBlocked: checkPostHogBlocked,
};
